// Superadmin-only custom provider templates (feature
// `custom-provider-templates`). Shares endpoint addresses platform-wide;
// test keys are request-scoped only and never stored.

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  createProviderTemplate,
  deleteProviderTemplate,
  listProviderTemplates,
  listTemplateCatalog,
  listTemplateModels,
  type ProviderCatalogLists,
  type ProviderTemplateTestResult,
  testProviderTemplate,
  updateProviderTemplate,
} from '../../api/providers'
import { useDialog } from '../../components/dialogs/useDialog'
import ModelPicker from '../../components/ModelPicker'
import {
  ExtraBodyInput,
  parseExtraBodyInput,
  stringifyExtraBody,
} from '../../components/ProviderLists'
import type { ProviderTemplate } from '../../types/api'
import { appendModelVariable, suggestTemplateUrls } from '../../utils/template-url-presets'

const inputClassName =
  'w-full px-4 py-3 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent'

const blankForm = {
  name: '',
  baseUrl: '',
  embeddingUrl: '',
  rerankUrl: '',
  chatUrl: '',
  modelsUrl: '',
  extraBody: '',
  embeddingModel: '',
  chatModel: '',
  rerankModel: '',
  apiKey: '',
}

/**
 * A Models-API value is a provider catalog (not an OpenAI `GET {root}/models`
 * listing) when the path ends with `/models/list` — e.g. DeepInfra's catalog.
 * Plain roots (`…/v1`) and explicit `…/models` URLs use the OpenAI listing.
 */
function isCatalogUrl(url: string): boolean {
  const trimmed = url.trim().replace(/\/+$/, '')
  if (!trimmed) return false
  try {
    const path = new URL(trimmed).pathname.replace(/\/+$/, '')
    return /\/models\/list$/i.test(path)
  } catch {
    return /\/models\/list$/i.test(trimmed)
  }
}

async function catalogList(
  kind: keyof ProviderCatalogLists,
  fetchCatalog: () => Promise<ProviderCatalogLists>
): Promise<string[]> {
  return (await fetchCatalog())[kind]
}

function ProbeBadge({ ok, skipped }: { ok: boolean; skipped?: boolean }) {
  if (skipped) {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-harbor-border/60 text-harbor-text-muted">
        skipped
      </span>
    )
  }
  return ok ? (
    <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300">ok</span>
  ) : (
    <span className="text-xs px-1.5 py-0.5 rounded bg-red-900/40 text-red-300">failed</span>
  )
}

export default function ProviderTemplatesPage() {
  const dialog = useDialog()
  const [templates, setTemplates] = useState<ProviderTemplate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState(blankForm)
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [testResult, setTestResult] = useState<ProviderTemplateTestResult | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      setTemplates(await listProviderTemplates())
    } catch {
      toast.error('Failed to load provider templates')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const set = (key: keyof typeof blankForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }))

  const autoFill = () => {
    const base = form.baseUrl.trim()
    if (!base) {
      toast.error('Enter a base URL first')
      return
    }
    // Host-aware suggestions (DeepInfra splits services across two roots);
    // never overwrites fields the admin already typed.
    const suggested = suggestTemplateUrls(base)
    setForm((f) => ({
      ...f,
      embeddingUrl: f.embeddingUrl.trim() || suggested.embeddingUrl,
      rerankUrl: f.rerankUrl.trim() || suggested.rerankUrl,
      chatUrl: f.chatUrl.trim() || suggested.chatUrl,
      modelsUrl: f.modelsUrl.trim() || suggested.modelsUrl,
    }))
  }

  /** Append a {model} variable to one service-URL field (slash-aware). */
  const insertModelVariable = (key: 'embeddingUrl' | 'rerankUrl' | 'chatUrl') => {
    setForm((f) => {
      const current = f[key].trim() || f.baseUrl.trim()
      if (!current) {
        toast.error('Enter a base URL first')
        return f
      }
      return { ...f, [key]: appendModelVariable(current) }
    })
  }

  const handleTest = async () => {
    if (!form.baseUrl.trim()) {
      toast.error('Enter a base URL first')
      return
    }
    let parsedExtra: Record<string, unknown> | null = null
    try {
      parsedExtra = parseExtraBodyInput(form.extraBody)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Invalid extra body JSON')
      return
    }
    setIsTesting(true)
    setTestResult(null)
    try {
      const result = await testProviderTemplate({
        baseUrl: form.baseUrl.trim(),
        embeddingUrl: form.embeddingUrl.trim() || null,
        rerankUrl: form.rerankUrl.trim() || null,
        chatUrl: form.chatUrl.trim() || null,
        modelsUrl: form.modelsUrl.trim() || null,
        apiKey: form.apiKey.trim() || null,
        embeddingModel: form.embeddingModel.trim() || null,
        chatModel: form.chatModel.trim() || null,
        rerankModel: form.rerankModel.trim() || null,
        extraBody: parsedExtra,
      })
      setTestResult(result)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Template test failed')
    } finally {
      setIsTesting(false)
    }
  }

  const openCreate = () => {
    setEditingId(null)
    setForm(blankForm)
    setTestResult(null)
    setShowForm(true)
  }

  const openEdit = (t: ProviderTemplate) => {
    setEditingId(t.id)
    setForm({
      name: t.name,
      baseUrl: t.baseUrl,
      embeddingUrl: t.embeddingUrl === t.baseUrl ? '' : t.embeddingUrl,
      rerankUrl: t.rerankUrl === t.baseUrl ? '' : t.rerankUrl,
      chatUrl: t.chatUrl === t.baseUrl ? '' : t.chatUrl,
      modelsUrl: t.modelsUrl === t.baseUrl ? '' : t.modelsUrl,
      extraBody: stringifyExtraBody(t.extraBody),
      embeddingModel: t.embeddingModel ?? '',
      chatModel: t.chatModel ?? '',
      rerankModel: t.rerankModel ?? '',
      apiKey: '',
    })
    setTestResult(null)
    setShowForm(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    let parsedExtra: Record<string, unknown> | null = null
    try {
      parsedExtra = parseExtraBodyInput(form.extraBody)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Invalid extra body JSON')
      return
    }
    setIsSaving(true)
    try {
      const payload = {
        name: form.name.trim(),
        baseUrl: form.baseUrl.trim(),
        embeddingUrl: form.embeddingUrl.trim() || null,
        rerankUrl: form.rerankUrl.trim() || null,
        chatUrl: form.chatUrl.trim() || null,
        modelsUrl: form.modelsUrl.trim() || null,
        embeddingModel: form.embeddingModel.trim() || null,
        chatModel: form.chatModel.trim() || null,
        rerankModel: form.rerankModel.trim() || null,
        extraBody: parsedExtra,
      }
      if (editingId === null) {
        await createProviderTemplate(payload)
        toast.success('Provider template added — available to all org admins')
      } else {
        await updateProviderTemplate(editingId, payload)
        toast.success('Provider template updated')
      }
      setShowForm(false)
      setForm(blankForm)
      setTestResult(null)
      await load()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save template')
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleActive = async (t: ProviderTemplate) => {
    try {
      await updateProviderTemplate(t.id, { isActive: !t.isActive })
      await load()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update template')
    }
  }

  const handleDelete = async (t: ProviderTemplate) => {
    const confirmed = await dialog.confirm({
      title: 'Delete provider template?',
      description: `Delete "${t.name}"? Org provider rows created from it keep working (they hold their own URLs).`,
      confirmLabel: 'Delete template',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await deleteProviderTemplate(t.id)
      toast.success('Provider template deleted')
      await load()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Cannot delete this template')
    }
  }

  const probeRows: Array<{ label: string; key: keyof ProviderTemplateTestResult }> = [
    { label: 'Models', key: 'models' },
    { label: 'Embedding', key: 'embedding' },
    { label: 'Chat', key: 'chat' },
    { label: 'Rerank', key: 'rerank' },
  ]

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-harbor-text">Provider Templates</h1>
          <p className="text-sm text-harbor-text-muted mt-1">
            Shared endpoint addresses for all organizations. Org admins pick a template to pre-fill
            URLs and add their own API key — keys are never shared or stored here.
          </p>
        </div>
        <button
          type="button"
          onClick={() => (showForm ? setShowForm(false) : openCreate())}
          className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white"
        >
          {showForm ? 'Cancel' : 'Add new provider'}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-harbor-text-muted">Loading…</p>
      ) : templates.length === 0 && !showForm ? (
        <p className="text-sm text-harbor-text-muted">
          No providers added yet. Use “Add new provider” to share an inference host with every
          organization.
        </p>
      ) : (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-harbor-text-muted">
            Added providers ({templates.length})
          </h2>
          {templates.map((t) => (
            <div key={t.id} className="rounded-lg border border-harbor-border p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-[10rem]">
                  <div className="font-medium text-harbor-text flex items-center gap-2">
                    {t.name}
                    {!t.isActive && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-harbor-border/60 text-harbor-text-muted">
                        inactive
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-harbor-text-muted truncate">{t.baseUrl}</div>
                </div>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={() => openEdit(t)}
                  className="px-2.5 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => void handleToggleActive(t)}
                  className="px-2.5 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text"
                >
                  {t.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete(t)}
                  className="px-2.5 py-1.5 text-xs rounded-lg bg-red-900/30 hover:bg-red-900/50 text-red-300"
                >
                  Delete
                </button>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-harbor-text-muted">
                <div className="truncate">
                  <dt className="inline text-harbor-text-secondary">Embedding: </dt>
                  <dd className="inline">{t.embeddingUrl}</dd>
                </div>
                <div className="truncate">
                  <dt className="inline text-harbor-text-secondary">Rerank: </dt>
                  <dd className="inline">{t.rerankUrl}</dd>
                </div>
                <div className="truncate">
                  <dt className="inline text-harbor-text-secondary">Chat: </dt>
                  <dd className="inline">{t.chatUrl}</dd>
                </div>
                <div className="truncate">
                  <dt className="inline text-harbor-text-secondary">Models: </dt>
                  <dd className="inline">{t.modelsUrl}</dd>
                </div>
                {t.embeddingModel && (
                  <div className="truncate sm:col-span-2">
                    <dt className="inline text-harbor-text-secondary">Embedding model: </dt>
                    <dd className="inline font-mono">{t.embeddingModel}</dd>
                  </div>
                )}
                {t.chatModel && (
                  <div className="truncate sm:col-span-2">
                    <dt className="inline text-harbor-text-secondary">Chat model: </dt>
                    <dd className="inline font-mono">{t.chatModel}</dd>
                  </div>
                )}
                {t.rerankModel && (
                  <div className="truncate sm:col-span-2">
                    <dt className="inline text-harbor-text-secondary">Rerank model: </dt>
                    <dd className="inline font-mono">{t.rerankModel}</dd>
                  </div>
                )}
              </dl>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleSave}
          className="space-y-4 rounded-xl border border-harbor-border bg-harbor-surface/40 p-6"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="pt-name">
                Name
              </label>
              <input
                id="pt-name"
                className={inputClassName}
                value={form.name}
                onChange={set('name')}
                required
                placeholder="e.g. Company vLLM gateway"
              />
            </div>
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="pt-base">
                Base URL
              </label>
              <div className="flex gap-2">
                <input
                  id="pt-base"
                  className={inputClassName}
                  value={form.baseUrl}
                  onChange={set('baseUrl')}
                  required
                  placeholder="https://ai.example.com/v1"
                />
                <button
                  type="button"
                  onClick={autoFill}
                  className="px-3 text-sm rounded-lg border border-harbor-border text-harbor-text hover:bg-harbor-surface-raised whitespace-nowrap"
                >
                  Auto-fill
                </button>
              </div>
            </div>
          </div>

          <p className="text-xs text-harbor-text-muted">
            Store <strong className="text-harbor-text-secondary">roots</strong> (`…/v1`) and the app
            appends the service path — or paste a complete endpoint URL and it is used as-is.{' '}
            <code className="text-harbor-text">{'{model}'}</code> /{' '}
            <code className="text-harbor-text">{'{model_name}'}</code> are substituted with the
            model name. DeepInfra example: base{' '}
            <code className="text-harbor-text">https://api.deepinfra.com/v1/openai</code>, rerank
            URL{' '}
            <code className="text-harbor-text">
              https://api.deepinfra.com/v1/inference/{'{model}'}
            </code>
            . Blank = same as base.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {(
              [
                ['embeddingUrl', 'Embedding URL'],
                ['rerankUrl', 'Rerank URL'],
                ['chatUrl', 'Chat completion URL'],
                ['modelsUrl', 'Models API URL'],
              ] as const
            ).map(([key, label]) => (
              <div key={key}>
                <label
                  className="block text-xs text-harbor-text-secondary mb-1"
                  htmlFor={`pt-${key}`}
                >
                  {label}
                </label>
                <input
                  id={`pt-${key}`}
                  className={inputClassName}
                  value={form[key]}
                  onChange={set(key)}
                  placeholder="Blank = same as base"
                />
                {key !== 'modelsUrl' && (
                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => insertModelVariable(key)}
                      className="px-2 py-0.5 text-[11px] rounded-full border border-harbor-border text-harbor-text-muted hover:text-harbor-text hover:border-harbor-border/80"
                      title="Append a {model} variable (substituted with the model name at request time)"
                    >
                      + {'{model}'}
                    </button>
                    <span className="text-[11px] text-harbor-text-muted self-center">
                      {'{model}'} / {'{model_name}'} are replaced with the model name
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <ExtraBodyInput
            id="pt-extra"
            value={form.extraBody}
            onChange={(v) => setForm((f) => ({ ...f, extraBody: v }))}
          />

          <div>
            <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="pt-key">
              Temporary test key — never stored
            </label>
            <input
              id="pt-key"
              type="password"
              className={inputClassName}
              value={form.apiKey}
              onChange={set('apiKey')}
              autoComplete="off"
              placeholder="Used for Load models + Test endpoints below"
            />
          </div>

          <div className="space-y-4">
            <ModelPicker
              apiBaseUrl={form.modelsUrl.trim() || form.baseUrl}
              apiKey={form.apiKey}
              value={form.embeddingModel}
              onChange={(v) => setForm((f) => ({ ...f, embeddingModel: v }))}
              inputClassName={inputClassName}
              id="pt-embedding-model"
              kind="embedding"
              loadModels={async (d) => {
                const catalogUrl = (d.apiBaseUrl || '').trim()
                if (isCatalogUrl(catalogUrl)) {
                  return catalogList('embedding', () => listTemplateCatalog(catalogUrl))
                }
                return listTemplateModels({ modelsUrl: d.apiBaseUrl, apiKey: d.apiKey ?? null })
              }}
              required={false}
            />
            <ModelPicker
              apiBaseUrl={form.modelsUrl.trim() || form.baseUrl}
              apiKey={form.apiKey}
              value={form.chatModel}
              onChange={(v) => setForm((f) => ({ ...f, chatModel: v }))}
              inputClassName={inputClassName}
              id="pt-chat-model"
              kind="llm"
              loadModels={async (d) => {
                const catalogUrl = (d.apiBaseUrl || '').trim()
                if (isCatalogUrl(catalogUrl)) {
                  return catalogList('llm', () => listTemplateCatalog(catalogUrl))
                }
                return listTemplateModels({ modelsUrl: d.apiBaseUrl, apiKey: d.apiKey ?? null })
              }}
              required={false}
            />
            <ModelPicker
              apiBaseUrl={form.modelsUrl.trim() || form.baseUrl}
              apiKey={form.apiKey}
              value={form.rerankModel}
              onChange={(v) => setForm((f) => ({ ...f, rerankModel: v }))}
              inputClassName={inputClassName}
              id="pt-rerank-model"
              kind="rerank"
              loadModels={async (d) => {
                const catalogUrl = (d.apiBaseUrl || '').trim()
                if (isCatalogUrl(catalogUrl)) {
                  return catalogList('rerank', () => listTemplateCatalog(catalogUrl))
                }
                return listTemplateModels({ modelsUrl: d.apiBaseUrl, apiKey: d.apiKey ?? null })
              }}
              required={false}
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void handleTest()}
              disabled={isTesting}
              className="px-3 py-1.5 text-sm rounded-lg border border-harbor-border text-harbor-text hover:bg-harbor-surface-raised disabled:opacity-50"
            >
              {isTesting ? 'Testing…' : 'Test endpoints'}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : editingId === null ? 'Save template' : 'Update template'}
            </button>
          </div>

          {testResult && (
            <div className="rounded-lg border border-harbor-border p-4 space-y-2">
              {probeRows.map(({ label, key }) => {
                const probe = testResult[key]
                const skipped = probe.message?.startsWith('skipped') ?? false
                return (
                  <div key={key} className="text-sm">
                    <span className="text-harbor-text font-medium">{label} </span>
                    <ProbeBadge ok={probe.ok} skipped={skipped} />
                    {key === 'rerank' && probe.detectedType && (
                      <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-sky-900/40 text-sky-300">
                        detected: {probe.detectedType} — use this provider type for rerank rows
                      </span>
                    )}
                    {probe.dimensions !== undefined && (
                      <span className="text-harbor-text-muted"> · {probe.dimensions}d</span>
                    )}
                    {probe.message && !skipped && (
                      <span className="text-harbor-text-muted"> · {probe.message}</span>
                    )}
                    {key === 'models' && probe.models && probe.models.length > 0 && (
                      <pre className="mt-1 text-xs text-harbor-text-muted bg-harbor-surface rounded p-2 overflow-auto max-h-40">
                        {JSON.stringify(probe.models, null, 2)}
                      </pre>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </form>
      )}
    </div>
  )
}
